/**
 * Pipebase brand mark — the gradient tile.
 *
 * Shared between the designer toolbar and (when wanted) other places.
 * When the active workspace has a custom logoUrl + brandName via
 * useBranding(), the mark renders that logo instead of the platform
 * default tile (customization US-003).
 *
 * The dashboard sidebar deliberately renders without it: per design,
 * the platform mark belongs to the designer surface, not the management
 * chrome.
 */
import { useBranding } from '../runtime/WorkspaceBrandingContext';

type Size = 'sm' | 'md';

interface BrandMarkProps {
  size?: Size;
  /** When true, renders the mark + the "Pipebase" wordmark side by side. */
  withWordmark?: boolean;
  className?: string;
}

const SIZES: Record<Size, { box: number; font: number; radius: number }> = {
  sm: { box: 22, font: 10, radius: 6 },
  md: { box: 26, font: 11.5, radius: 7 },
};

export function BrandMark({ size = 'md', withWordmark = false, className = '' }: BrandMarkProps) {
  const s = SIZES[size];
  const { branding, brandName } = useBranding();

  // Workspace has a custom logo → render that instead of the gradient tile.
  if (branding.logoUrl) {
    return (
      <span
        className={className}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
      >
        <img
          src={branding.logoUrl}
          alt={brandName}
          style={{
            height: s.box,
            width: 'auto',
            maxWidth: s.box * 4,
            objectFit: 'contain',
            display: 'block',
          }}
        />
        {withWordmark && brandName && (
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: 'var(--text-1)',
            }}
          >
            {brandName}
          </span>
        )}
      </span>
    );
  }

  // Platform default — gradient tile + optional wordmark using the
  // brand name from useBranding so a brandName-only override (no logo)
  // still wins the wordmark slot.
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
      }}
    >
      <span
        aria-label={brandName}
        style={{
          width: s.box,
          height: s.box,
          borderRadius: s.radius,
          // --brand-primary / --brand-secondary win when set by the
          // active workspace's branding; we fall through to the
          // platform palette otherwise.
          background:
            'radial-gradient(circle at 30% 28%, rgba(255,255,255,0.18), transparent 60%), linear-gradient(135deg, var(--brand-primary, var(--brand-400)), var(--brand-secondary, var(--brand-600)))',
          display: 'grid',
          placeItems: 'center',
          color: 'white',
          fontWeight: 700,
          fontSize: s.font,
          letterSpacing: '-0.04em',
          boxShadow:
            '0 0 0 1px rgba(255,255,255,0.05) inset, 0 1px 0 rgba(255,255,255,0.18) inset',
        }}
      >
        42
      </span>
      {withWordmark && (
        <span
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: 'var(--text-1)',
          }}
        >
          {brandName}
        </span>
      )}
    </span>
  );
}
